import { useState, useRef, useCallback } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Plus, Trash2, Car, Shield, Award, Upload, Loader2, Eye, ChevronDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import type { SpecialCertification } from "@/types/hr";

const DRIVER_LICENSE_CLASSES = [
  { value: "AM", label: "AM (Kleinkrafträder)" },
  { value: "A1", label: "A1 (Leichtkrafträder)" },
  { value: "A2", label: "A2 (Krafträder)" },
  { value: "A", label: "A (Alle Krafträder)" },
  { value: "B", label: "B (PKW)" },
  { value: "BE", label: "BE (PKW mit Anhänger)" },
  { value: "B96", label: "B96 (PKW mit schwerem Anhänger)" },
  { value: "C1", label: "C1 (LKW bis 7,5t)" },
  { value: "C1E", label: "C1E (LKW bis 7,5t mit Anhänger)" },
  { value: "C", label: "C (LKW)" },
  { value: "CE", label: "CE (LKW mit Anhänger)" },
  { value: "D1", label: "D1 (Kleinbusse)" },
  { value: "D1E", label: "D1E (Kleinbusse mit Anhänger)" },
  { value: "D", label: "D (Busse)" },
  { value: "DE", label: "DE (Busse mit Anhänger)" },
  { value: "L", label: "L (Zugmaschinen)" },
  { value: "T", label: "T (Land-/Forstwirtschaft)" },
];

const CERTIFICATION_TYPES = [
  { value: "forklift", label: "Staplerschein" },
  { value: "aerial_platform", label: "Hebebühnenschein" },
  { value: "crane", label: "Kranschein" },
  { value: "welding", label: "Schweißerschein" },
  { value: "electrical", label: "Elektrofachkraft" },
  { value: "first_aid", label: "Ersthelfer" },
  { value: "fire_safety", label: "Brandschutzhelfer" },
  { value: "hazmat", label: "Gefahrgut (ADR)" },
  { value: "other", label: "Sonstige" },
];

interface LicenseAndCertificationsFormProps {
  employeeId?: string;
  applicantId?: string;
  driversLicenseClasses: string[];
  specialCertifications: SpecialCertification[];
  securityClearanceValidUntil?: string;
  securityClearanceDocumentId?: string;
  onDriversLicenseChange: (classes: string[]) => void;
  onCertificationsChange: (certifications: SpecialCertification[]) => void;
  onSecurityClearanceChange: (date: string) => void;
  onSecurityClearanceDocumentChange?: (documentId: string | undefined) => void;
}

export function LicenseAndCertificationsForm({
  employeeId,
  applicantId,
  driversLicenseClasses,
  specialCertifications,
  securityClearanceValidUntil,
  securityClearanceDocumentId,
  onDriversLicenseChange,
  onCertificationsChange,
  onSecurityClearanceChange,
  onSecurityClearanceDocumentChange,
}: LicenseAndCertificationsFormProps) {
  const { user } = useAuth();
  const [newCertType, setNewCertType] = useState("");
  const [newCertName, setNewCertName] = useState("");
  const [newCertValidUntil, setNewCertValidUntil] = useState("");
  const [uploadingCertIndex, setUploadingCertIndex] = useState<number | null>(null);
  const [uploadingSecurityDoc, setUploadingSecurityDoc] = useState(false);
  const [certUploadDialogIndex, setCertUploadDialogIndex] = useState<number | null>(null);
  const [securityUploadDialogOpen, setSecurityUploadDialogOpen] = useState(false);
  const [dragOverCert, setDragOverCert] = useState<number | null>(null);
  const [dragOverSecurity, setDragOverSecurity] = useState(false);
  const [licensePopoverOpen, setLicensePopoverOpen] = useState(false);
  const dialogCertFileInputRef = useRef<HTMLInputElement>(null);
  const dialogSecurityFileInputRef = useRef<HTMLInputElement>(null);

  const handleLicenseToggle = (licenseClass: string) => {
    if (driversLicenseClasses.includes(licenseClass)) {
      onDriversLicenseChange(driversLicenseClasses.filter(c => c !== licenseClass));
    } else {
      onDriversLicenseChange([...driversLicenseClasses, licenseClass]);
    }
  };

  const handleAddCertification = () => {
    if (!newCertType) return;
    
    const certType = CERTIFICATION_TYPES.find(t => t.value === newCertType);
    const name = newCertType === "other" && newCertName ? newCertName : certType?.label || newCertType;
    
    const newCert: SpecialCertification = {
      type: newCertType,
      name: name,
      valid_until: newCertValidUntil || undefined,
    };
    
    onCertificationsChange([...specialCertifications, newCert]);
    setNewCertType("");
    setNewCertName("");
    setNewCertValidUntil("");
  };

  const handleRemoveCertification = (index: number) => {
    onCertificationsChange(specialCertifications.filter((_, i) => i !== index));
  };

  const handleUpdateCertificationValidity = (index: number, validUntil: string) => {
    const updated = [...specialCertifications];
    updated[index] = { ...updated[index], valid_until: validUntil || undefined };
    onCertificationsChange(updated);
  };

  const uploadDocument = async (
    file: File,
    category: string,
    description: string
  ): Promise<string | null> => {
    if (!user || (!employeeId && !applicantId)) return null;

    const isEmployee = !!employeeId;
    const entityId = employeeId || applicantId;
    const bucket = isEmployee ? "employee-documents" : "applicant-documents";

    const filePath = `${entityId}/${Date.now()}-${file.name}`;

    const { error: uploadError } = await supabase.storage
      .from(bucket)
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    if (isEmployee) {
      const { data, error: insertError } = await supabase
        .from("employee_documents")
        .insert({
          employee_id: entityId,
          file_name: file.name,
          file_path: filePath,
          file_size: file.size,
          mime_type: file.type,
          category: category,
          description: description,
          uploaded_by: user.id,
        })
        .select("id")
        .single();

      if (insertError) throw insertError;
      return data?.id || null;
    } else {
      const { data, error: insertError } = await supabase
        .from("applicant_documents")
        .insert({
          applicant_id: entityId!,
          file_name: file.name,
          file_path: filePath,
          file_size: file.size,
          mime_type: file.type,
          category: category,
          description: description,
          uploaded_by: user.id,
        })
        .select("id")
        .single();

      if (insertError) throw insertError;
      return data?.id || null;
    }
  };

  const handleCertificationFileUpload = async (file: File, index: number) => {
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error("Datei ist zu groß. Maximal 10MB erlaubt.");
      return;
    }

    setUploadingCertIndex(index);
    setCertUploadDialogIndex(null);
    try {
      const cert = specialCertifications[index];
      const documentId = await uploadDocument(
        file,
        "Zertifikate",
        `${cert.name} - ${cert.valid_until || "Unbefristet"}`
      );

      if (documentId) {
        const updated = [...specialCertifications];
        updated[index] = { ...updated[index], document_id: documentId };
        onCertificationsChange(updated);
        toast.success("Dokument erfolgreich hochgeladen");
      }
    } catch (error: any) {
      console.error("Error uploading document:", error);
      toast.error(error.message || "Fehler beim Hochladen");
    } finally {
      setUploadingCertIndex(null);
    }
  };

  const handleSecurityClearanceFileUpload = async (file: File) => {
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error("Datei ist zu groß. Maximal 10MB erlaubt.");
      return;
    }

    setUploadingSecurityDoc(true);
    setSecurityUploadDialogOpen(false);
    try {
      const documentId = await uploadDocument(
        file,
        "Zertifikate",
        `Sicherheitsüberprüfung - Gültig bis ${securityClearanceValidUntil || "unbekannt"}`
      );

      if (documentId && onSecurityClearanceDocumentChange) {
        onSecurityClearanceDocumentChange(documentId);
        toast.success("Dokument erfolgreich hochgeladen");
      }
    } catch (error: any) {
      console.error("Error uploading document:", error);
      toast.error(error.message || "Fehler beim Hochladen");
    } finally {
      setUploadingSecurityDoc(false);
    }
  };

  const handleCertDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverCert(index);
  }, []);

  const handleCertDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverCert(null);
  }, []);

  const handleCertDrop = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverCert(null);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleCertificationFileUpload(files[0], index);
    }
  }, [specialCertifications]);

  const handleSecurityDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverSecurity(true);
  }, []);

  const handleSecurityDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverSecurity(false);
  }, []);

  const handleSecurityDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverSecurity(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleSecurityClearanceFileUpload(files[0]);
    }
  }, [securityClearanceValidUntil, onSecurityClearanceDocumentChange]);

  const handleViewDocument = async (documentId: string) => {
    const isEmployee = !!employeeId;
    const bucket = isEmployee ? "employee-documents" : "applicant-documents";
    const table = isEmployee ? "employee_documents" : "applicant_documents";

    try {
      const { data: doc, error } = await supabase
        .from(table)
        .select("file_path")
        .eq("id", documentId)
        .single();

      if (error) throw error;

      const { data: signedUrl, error: urlError } = await supabase.storage
        .from(bucket)
        .createSignedUrl(doc.file_path, 3600);

      if (urlError) throw urlError;

      window.open(signedUrl.signedUrl, "_blank");
    } catch (error) {
      console.error("Error viewing document:", error);
      toast.error("Fehler beim Öffnen des Dokuments");
    }
  };

  const canUploadDocuments = !!employeeId || !!applicantId;

  return (
    <div className="space-y-6">
      {/* Driver's License */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Car className="h-4 w-4" />
            Führerscheinklassen
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Popover open={licensePopoverOpen} onOpenChange={setLicensePopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                className="w-full justify-between text-left font-normal"
              >
                {driversLicenseClasses.length > 0
                  ? `${driversLicenseClasses.length} Klasse(n) ausgewählt: ${driversLicenseClasses.join(", ")}`
                  : "Führerscheinklassen auswählen..."}
                <ChevronDown className="h-4 w-4 ml-2 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[320px] p-3 bg-background border z-50" align="start">
              <div className="grid grid-cols-3 gap-2">
                {DRIVER_LICENSE_CLASSES.map((license) => (
                  <label
                    key={license.value}
                    className="flex items-center gap-2 cursor-pointer hover:bg-muted p-1 rounded"
                  >
                    <Checkbox
                      checked={driversLicenseClasses.includes(license.value)}
                      onCheckedChange={() => handleLicenseToggle(license.value)}
                    />
                    <span className="text-sm">{license.value}</span>
                  </label>
                ))}
              </div>
              {driversLicenseClasses.length > 0 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => onDriversLicenseChange([])}
                  className="w-full mt-3 text-muted-foreground hover:text-foreground"
                >
                  Auswahl zurücksetzen
                </Button>
              )}
            </PopoverContent>
          </Popover>
        </CardContent>
      </Card>

      {/* Special Certifications */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Award className="h-4 w-4" />
            Sonderbescheinigungen
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Existing certifications */}
          {specialCertifications.length > 0 && (
            <div className="space-y-2">
              {specialCertifications.map((cert, index) => (
                <div key={index} className="flex flex-wrap items-center gap-3 p-3 bg-muted rounded-md">
                  <div className="flex-1 min-w-[150px]">
                    <span className="font-medium text-sm">{cert.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm whitespace-nowrap">Gültig bis:</Label>
                    <Input
                      type="date"
                      value={cert.valid_until || ""}
                      onChange={(e) => handleUpdateCertificationValidity(index, e.target.value)}
                      className="w-40"
                    />
                  </div>
                  {/* Document upload/view with drag-and-drop */}
                  {canUploadDocuments && (
                    <div className="flex items-center gap-1">
                      {cert.document_id ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewDocument(cert.document_id!)}
                          className="gap-1"
                        >
                          <Eye className="h-3 w-3" />
                          Dokument
                        </Button>
                      ) : (
                        <div
                          onDragOver={(e) => handleCertDragOver(e, index)}
                          onDragLeave={handleCertDragLeave}
                          onDrop={(e) => handleCertDrop(e, index)}
                          onClick={() => setCertUploadDialogIndex(index)}
                          className={cn(
                            "border-2 border-dashed rounded-md px-3 py-2 cursor-pointer transition-colors flex items-center gap-2",
                            dragOverCert === index
                              ? "border-primary bg-primary/10"
                              : "border-muted-foreground/30 hover:border-muted-foreground/50"
                          )}
                        >
                          {uploadingCertIndex === index ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Upload className="h-3 w-3" />
                          )}
                          <span className="text-xs">Hierher ziehen oder klicken</span>
                        </div>
                      )}
                    </div>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveCertification(index)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Add new certification */}
          <div className="flex flex-wrap gap-3 items-end border-t pt-4">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-sm">Bescheinigung hinzufügen</Label>
              <Select value={newCertType} onValueChange={setNewCertType}>
                <SelectTrigger>
                  <SelectValue placeholder="Typ auswählen..." />
                </SelectTrigger>
                <SelectContent>
                  {CERTIFICATION_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {newCertType === "other" && (
              <div className="flex-1 min-w-[200px]">
                <Label className="text-sm">Bezeichnung</Label>
                <Input
                  value={newCertName}
                  onChange={(e) => setNewCertName(e.target.value)}
                  placeholder="Name der Bescheinigung"
                />
              </div>
            )}
            
            <div className="w-40">
              <Label className="text-sm">Gültig bis</Label>
              <Input
                type="date"
                value={newCertValidUntil}
                onChange={(e) => setNewCertValidUntil(e.target.value)}
              />
            </div>
            
            <Button
              type="button"
              variant="outline"
              onClick={handleAddCertification}
              disabled={!newCertType || (newCertType === "other" && !newCertName)}
            >
              <Plus className="h-4 w-4 mr-1" />
              Hinzufügen
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Security Clearance */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Sicherheitsüberprüfung
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 max-w-xs">
              <Label>Gültig bis</Label>
              <Input
                type="date"
                value={securityClearanceValidUntil || ""}
                onChange={(e) => onSecurityClearanceChange(e.target.value)}
              />
            </div>
            {securityClearanceValidUntil && (
              <div className="flex items-center gap-2 mb-2">
                <div className={`h-3 w-3 rounded-full ${
                  new Date(securityClearanceValidUntil) > new Date() 
                    ? "bg-green-500" 
                    : "bg-red-500"
                }`} />
                <span className="text-sm">
                  {new Date(securityClearanceValidUntil) > new Date() 
                    ? "Gültig" 
                    : "Abgelaufen"}
                </span>
              </div>
            )}
            {/* Document upload/view for security clearance with drag-and-drop */}
            {canUploadDocuments && (
              <div className="flex items-center gap-2 mb-2">
                {securityClearanceDocumentId ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewDocument(securityClearanceDocumentId)}
                    className="gap-1"
                  >
                    <Eye className="h-3 w-3" />
                    Dokument anzeigen
                  </Button>
                ) : (
                  <div
                    onDragOver={handleSecurityDragOver}
                    onDragLeave={handleSecurityDragLeave}
                    onDrop={handleSecurityDrop}
                    onClick={() => setSecurityUploadDialogOpen(true)}
                    className={cn(
                      "border-2 border-dashed rounded-md px-3 py-2 cursor-pointer transition-colors flex items-center gap-2",
                      dragOverSecurity
                        ? "border-primary bg-primary/10"
                        : "border-muted-foreground/30 hover:border-muted-foreground/50"
                    )}
                  >
                    {uploadingSecurityDoc ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Upload className="h-3 w-3" />
                    )}
                    <span className="text-xs">Nachweis hierher ziehen oder klicken</span>
                  </div>
                )}
              </div>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Leer lassen, wenn keine Sicherheitsüberprüfung vorliegt.
          </p>
        </CardContent>
      </Card>

      {/* Dialog for certification file upload */}
      <Dialog open={certUploadDialogIndex !== null} onOpenChange={(open) => !open && setCertUploadDialogIndex(null)}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Dokument hochladen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const files = e.dataTransfer.files;
                if (files.length > 0 && certUploadDialogIndex !== null) {
                  handleCertificationFileUpload(files[0], certUploadDialogIndex);
                }
              }}
              onClick={() => dialogCertFileInputRef.current?.click()}
              className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors hover:border-primary hover:bg-primary/5"
            >
              <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
              <p className="font-medium">Datei hierher ziehen</p>
              <p className="text-sm text-muted-foreground mt-1">oder klicken zum Auswählen</p>
              <p className="text-xs text-muted-foreground mt-2">PDF, JPG, PNG (max. 10MB)</p>
            </div>
            <input
              type="file"
              ref={dialogCertFileInputRef}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file && certUploadDialogIndex !== null) {
                  handleCertificationFileUpload(file, certUploadDialogIndex);
                }
                e.target.value = "";
              }}
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png"
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog for security clearance file upload */}
      <Dialog open={securityUploadDialogOpen} onOpenChange={setSecurityUploadDialogOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Sicherheitsüberprüfung Nachweis</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const files = e.dataTransfer.files;
                if (files.length > 0) {
                  handleSecurityClearanceFileUpload(files[0]);
                }
              }}
              onClick={() => dialogSecurityFileInputRef.current?.click()}
              className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors hover:border-primary hover:bg-primary/5"
            >
              <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
              <p className="font-medium">Datei hierher ziehen</p>
              <p className="text-sm text-muted-foreground mt-1">oder klicken zum Auswählen</p>
              <p className="text-xs text-muted-foreground mt-2">PDF, JPG, PNG (max. 10MB)</p>
            </div>
            <input
              type="file"
              ref={dialogSecurityFileInputRef}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  handleSecurityClearanceFileUpload(file);
                }
                e.target.value = "";
              }}
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
